import React, { useState, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { Typography } from '@mui/material'

// components
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import RejectionWrapperMachineWise from './FiRejectionWrapperMachineWise'

// service
import { getFiRejectionTable } from '../../service/fiRejectionReason.service'

// context
import { useDateSelectorContext } from '../../context/DateSelectorContext'

const FiRejection = () => {
  const { selectedDate } = useDateSelectorContext()

  const [isLoading, setIsLoading] = useState(false)
  const [rows, setRows] = useState([])

  // FILTER STATES
  const [shiftOptions, setShiftOptions] = useState([])
  const [workOrderOptions, setWorkOrderOptions] = useState([])

  // NEW FILTERS
  const [customerOptions, setCustomerOptions] = useState([])
  const [partNumberOptions, setPartNumberOptions] = useState([])

  const [selectedShift, setSelectedShift] = useState({
    label: 'All shifts',
    value: 0
  })

  const [selectedCustomer, setSelectedCustomer] = useState({
    label: 'All customers',
    value: 'all'
  })

  const [selectedPartNumber, setSelectedPartNumber] = useState({
    label: 'All part numbers',
    value: 'all'
  })

  const [selectedWorkOrder, setSelectedWorkOrder] = useState({
    label: 'All work orders',
    value: 'all'
  })

  const fetchData = async () => {
    const params = {}
    if (selectedDate) params.date = selectedDate

    try {
      setIsLoading(true)
      const response = await getFiRejectionTable(params)

      if (response.data.success) {
        const data = response.data.data
        setRows(data)

        const shifts = [...new Set(data.map((r) => `S${r.shift}`))]
        setShiftOptions([
          { label: 'All shifts', value: 0 },
          ...shifts.map((s) => ({ label: s, value: s }))
        ])

        const customers = [...new Set(data.map((r) => r.partName))]
        setCustomerOptions([
          { label: 'All customers', value: 'all' },
          ...customers.map((c) => ({ label: c, value: c }))
        ])

        const partTypes = [...new Set(data.map((r) => r.partType))]
        setPartNumberOptions([
          { label: 'All part numbers', value: 'all' },
          ...partTypes.map((p) => ({ label: p, value: p }))
        ])

        const workOrders = [...new Set(data.map((r) => r.workOrderNum))]
        setWorkOrderOptions([
          { label: 'All work orders', value: 'all' },
          ...workOrders.map((w) => ({ label: w, value: w }))
        ])
      }
    } catch (err) {
      console.log(err)
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [selectedDate])

  let filteredData = [...rows]

  const searchObj = {
    ...(selectedShift.value !== 0 && { shift: selectedShift.value }),
    ...(selectedCustomer.value !== 'all' && {
      partName: selectedCustomer.value
    }),
    ...(selectedPartNumber.value !== 'all' && {
      partType: selectedPartNumber.value
    }),
    ...(selectedWorkOrder.value !== 'all' && {
      workOrderNum: selectedWorkOrder.value
    })
  }

  // APPLY FILTERS
  const keys = Object.keys(searchObj)
  if (keys.length > 0) {
    filteredData = filteredData.filter((row) =>
      keys.every((key) => searchObj[key] === row[key])
    )
  }

  const columns = [
    { Header: 'Shift', accessor: 'shift' },
    { Header: 'Customer', accessor: 'partName' },
    { Header: 'Part number', accessor: 'partType' },
    { Header: 'Work order', accessor: 'workOrderNum' },
    { Header: 'No of trays', accessor: 'productionId' },
    { Header: 'Part count', accessor: 'partCount' },
    { Header: 'Rejection count', accessor: 'rejectionCount' },
    { Header: 'Operator', accessor: 'operator' },
    { Header: 'FI incharge', accessor: 'supervisor' }
  ]

  const totalPartCount = filteredData.reduce(
    (acc, r) => acc + Number(r.partCount),
    0
  )
  const totalRejectionCount = filteredData.reduce(
    (acc, r) => acc + Number(r.rejectionCount),
    0
  )

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee',
          zIndex: 20
        }}
      >
        <StyledSelectField
          placeHolderText="Select shift"
          selectedValue={selectedShift}
          selectOptions={shiftOptions}
          onChangeHandler={setSelectedShift}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No shift found"
        />

        <StyledSelectField
          placeHolderText="Select customer"
          selectedValue={selectedCustomer}
          selectOptions={customerOptions}
          onChangeHandler={setSelectedCustomer}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No customer found"
        />

        <StyledSelectField
          placeHolderText="Select part number"
          selectedValue={selectedPartNumber}
          selectOptions={partNumberOptions}
          onChangeHandler={setSelectedPartNumber}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No part number found"
        />

        <StyledSelectField
          placeHolderText="Select work order"
          selectedValue={selectedWorkOrder}
          selectOptions={workOrderOptions}
          onChangeHandler={setSelectedWorkOrder}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No work order num found"
        />
      </Stack>

      <Stack sx={{ padding: '16px', gap: '16px' }}>
        <Box sx={{ display: 'flex', gap: '32px' }}>
          <Typography>
            Total inspected count: <b>{totalPartCount}</b>
          </Typography>
          <Typography>
            Total rejection count: <b>{totalRejectionCount}</b>
          </Typography>
        </Box>

        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}

          {!isLoading && filteredData.length === 0 && (
            <StyledEmptyHeader>No FI rejection records.</StyledEmptyHeader>
          )}

          {!isLoading && filteredData.length > 0 && (
            <StyledTable
              pagination={false}
              columns={columns}
              data={filteredData}
            />
          )}
        </StyledTableContainer>
        <RejectionWrapperMachineWise
          selectedDate={selectedDate}
          selectedShift={selectedShift}
        />
      </Stack>
    </>
  )
}

export default FiRejection
